import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Skeleton, IconButton, Grid, Popover, Typography, Divider, InputAdornment, Link,
    CircularProgress, Radio, RadioGroup, FormControlLabel, Stack,
} from '@mui/material';
import {CompanyService} from '@/services/accountService';
import {
    CompanyInfo,
    CompanyInfoHistory,
    CrGovCompanyHit,
    normalizeIndustryIds,
} from '@/services/accountService/companyService';
import {CompanyIndustry, getCompanyIndustry, companySelectList} from '@/services/accountService/companyIndustry';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import {useTheme} from '@mui/material/styles';
import {SnackbarField, InputField, DatePicker, MultiSelectField} from '@/components/common';
import {usePageCrud} from '@/hooks/usePageCrud';
import {
    useCompanyCrGovLookup,
    CompanyCrValidateHandle,
} from '@/pages/components/company-info/company/useCompanyCrGovLookup';

interface Props {
    open: boolean;
    onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
    companyId?: number;
    inline?: boolean;
    // Explicit rendering mode. When set to 'content', only render the content and actions (no dialog or header).
    // Default: 'dialog' unless inline=true, then treated as content for backward-compat.
    renderMode?: 'dialog' | 'content';
    disableRegisteredOfficeValidation?: boolean;
    hideRegisteredOffice?: boolean;
    /** Hide industry field (FS create: industry set later on SalesIncomeWp) */
    hideIndustry?: boolean;
    initialData?: Partial<CompanyInfo>;
    /** 隐藏组件内的 Create/Save 按钮，由父组件通过 saveRef 触发保存；Cancel 仍显示 */
    hideActionButtons?: boolean;
    /** 暴露 save 函数给父组件，支持外部触发保存 */
    saveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
    /** Excel Validate：父组件触发 CR 校验（先 Popover 再写表单） */
    crValidateRef?: React.MutableRefObject<CompanyCrValidateHandle | null>;
}

export default function CompanyDetail({
    open, onClose, companyId, inline, renderMode, disableRegisteredOfficeValidation,
    hideRegisteredOffice, hideIndustry, initialData, hideActionButtons, saveRef, crValidateRef,
}: Props) {
    const theme = useTheme();
    const isEdit = !!companyId;
    const {canCreate, canUpdate} = usePageCrud({
        read: ['company_query', 'company_get'],
        create: 'account:company:create',
        update: 'account:company:update',
        remove: 'account:company:delete',
    });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'warning' | 'info'
    }>({open: false, message: '', severity: 'success'});
    const [form, setForm] = useState<CompanyInfo>({
        companyId: 0,
        companyName: '',
        companyChineseName: '',
        inCorpDate: '',
        industry: undefined,
        registeredOffice: '',
        br: '',
    });
    const formRef = useRef(form);
    formRef.current = form;
    const [fieldErrors, setFieldErrors] = useState<{ companyName?: string; industry?: string; registeredOffice?: string; inCorpDate?: string; br?: string; _global?: string }>({});
    const [industries, setIndustries] = useState<CompanyIndustry[]>([]);
    const [officeHistories, setOfficeHistories] = useState<CompanyInfoHistory[]>([]);
    const [companyNameHistories, setCompanyNameHistories] = useState<CompanyInfoHistory[]>([]);
    const [companyChineseNameHistories, setCompanyChineseNameHistories] = useState<CompanyInfoHistory[]>([]);
    const historyBtnRef = useRef<HTMLButtonElement>(null);
    const nameHistoryBtnRef = useRef<HTMLButtonElement>(null);
    const chineseNameHistoryBtnRef = useRef<HTMLButtonElement>(null);
    const crSearchBtnRef = useRef<HTMLButtonElement>(null);
    const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
    const [nameHistoryAnchorEl, setNameHistoryAnchorEl] = useState<HTMLElement | null>(null);
    const [chineseNameHistoryAnchorEl, setChineseNameHistoryAnchorEl] = useState<HTMLElement | null>(null);

    const applyCrHit = useCallback((hit: CrGovCompanyHit) => {
        // Convert：仅写表单状态，不调用保存 API
        setForm((prev) => ({
            ...prev,
            companyName: hit.companyName ?? prev.companyName,
            companyChineseName: hit.companyChineseName ?? prev.companyChineseName,
            inCorpDate: hit.incorporationDate ?? prev.inCorpDate,
            br: hit.brNo ?? prev.br,
        }));
    }, []);

    const crLookup = useCompanyCrGovLookup({
        getForm: () => ({
            companyName: formRef.current.companyName,
            companyChineseName: formRef.current.companyChineseName,
            inCorpDate: formRef.current.inCorpDate,
            br: formRef.current.br,
        }),
        applyHit: applyCrHit,
        showSnackbar: (message, severity = 'warning') => {
            setSnackbar({open: true, message, severity});
        },
    });

    useEffect(() => {
        if (!crValidateRef) return;
        crValidateRef.current = {
            validate: (anchorEl) => crLookup.validateFromExternal(anchorEl),
        };
        return () => {
            if (crValidateRef) crValidateRef.current = null;
        };
    }, [crValidateRef, crLookup.validateFromExternal]);

    // Resolve effective mode
    const mode: 'dialog' | 'content' = (renderMode ? renderMode : (inline ? 'content' : 'dialog'));
    const isActive = open || inline || mode === 'content';

    const loadDetail = async () => {
        if (!companyId) return;
        try {
            setLoading(true);
            const data = await CompanyService.getCompany(companyId);
            const base: CompanyInfo = {
                companyId: data.companyId ?? 0,
                companyName: data.companyName ?? '',
                companyChineseName: data.companyChineseName ?? '',
                inCorpDate: data.inCorpDate ?? '',
                industry: normalizeIndustryIds(data.industry),
                registeredOffice: data.registeredOffice ?? '',
                br: data.br ?? '',
            };
            // 当 initialData 存在时，将 Excel 导入数据覆盖到从数据库加载的基础数据上
            if (initialData) {
                if (initialData.companyName) base.companyName = initialData.companyName;
                if (initialData.industry !== undefined) base.industry = normalizeIndustryIds(initialData.industry);
                if (initialData.inCorpDate) base.inCorpDate = initialData.inCorpDate;
                if (initialData.registeredOffice) base.registeredOffice = initialData.registeredOffice;
                if (initialData.br) base.br = initialData.br;
                if (initialData.companyChineseName) base.companyChineseName = initialData.companyChineseName;
            }
            setForm(base);
            setOfficeHistories(Array.isArray(data.registeredOfficeHistories) ? data.registeredOfficeHistories : []);
            setCompanyNameHistories(Array.isArray(data.companyNameHistories) ? data.companyNameHistories : []);
            setCompanyChineseNameHistories(
                Array.isArray(data.companyChineseNameHistories) ? data.companyChineseNameHistories : [],
            );
        } catch {
            setOfficeHistories([]);
            setCompanyNameHistories([]);
            setCompanyChineseNameHistories([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isActive) {
            if (isEdit) loadDetail(); else {
                setForm({
                    companyId: 0,
                    companyName: initialData?.companyName ?? '',
                    companyChineseName: initialData?.companyChineseName ?? '',
                    inCorpDate: initialData?.inCorpDate ?? '',
                    industry: normalizeIndustryIds(initialData?.industry),
                    registeredOffice: initialData?.registeredOffice ?? '',
                    br: initialData?.br ?? '',
                });
                setOfficeHistories([]);
                setCompanyNameHistories([]);
                setCompanyChineseNameHistories([]);
                setHistoryAnchorEl(null);
                setNameHistoryAnchorEl(null);
                setChineseNameHistoryAnchorEl(null);
            }
            setFieldErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, companyId]);

    // Load industry options once when dialog opens via company selectList
    useEffect(() => {
        if (!isActive || hideIndustry) return;
        (async () => {
            try {
                const res = await companySelectList();
                let options: CompanyIndustry[] = [];
                if (Array.isArray(res)) {
                    options = res.map((it: any) => ({
                        industryId: it?.industryId ?? it?.id ?? it?.value,
                        industryName: it?.industryName ?? it?.name ?? it?.label,
                    })).filter((o: CompanyIndustry) => o.industryId != null);
                } else if (res && typeof res === 'object') {
                    options = Object.entries(res).map(([k, v]) => ({
                        industryId: Number(k),
                        industryName: String(v as any)
                    }));
                }
                setIndustries(options);
            } catch {
                setIndustries([]);
            }
        })();
    }, [isActive, hideIndustry]);

    // Ensure selected industry options exist for chip labels
    useEffect(() => {
        if (!isActive || hideIndustry) return;
        const ids = normalizeIndustryIds(form.industry);
        const missing = ids.filter((id) => !industries.some((i) => i.industryId === id));
        if (missing.length === 0) return;
        (async () => {
            for (const id of missing) {
                try {
                    const one = await getCompanyIndustry(id);
                    if (one && one.industryId != null) {
                        setIndustries((prev) =>
                            prev.some((i) => i.industryId === one.industryId) ? prev : [...prev, one],
                        );
                    }
                } catch {
                    // ignore missing dictionary row
                }
            }
        })();
    }, [isActive, hideIndustry, form.industry, industries]);

    // Validate BR field - if any content exists, left part must not be empty
    const validateBR = (br: string): boolean => {
        if (!br) return true; // BR is optional, so completely empty is valid
        // If there's any input (including just separator or right part), left part must not be empty
        const parts = br.split('-');
        return !!(parts[0] && parts[0].trim().length > 0); // Left part must not be empty
    };

    // Check if BR has any content (for error display)
    const hasBRContent = (br: string): boolean => {
        return !!(br && br.trim().length > 0);
    };

    // Shared with O1 Issued Capital via account_company.registered_office (either side updates → both show).
    const shouldValidateRegisteredOffice = !disableRegisteredOfficeValidation && !hideRegisteredOffice;
    const industryIds = normalizeIndustryIds(form.industry);
    const industryRequired = !hideIndustry;
    const industryMissing = industryRequired && industryIds.length === 0;

    const hasErrors = !form.companyName || industryMissing || (shouldValidateRegisteredOffice && !form.registeredOffice) || !form.inCorpDate ||
        !validateBR(form.br || '') ||
        !!fieldErrors.companyName || !!fieldErrors.industry || (shouldValidateRegisteredOffice && !!fieldErrors.registeredOffice) || !!fieldErrors.inCorpDate || !!fieldErrors.br;

    const onSave = async (): Promise<boolean> => {
        setSaving(true);
        try {
            if (!form) return false;
            
            // Validate required fields
            const errors: typeof fieldErrors = {};
            if (!form.companyName) errors.companyName = 'Company name is required';
            if (industryMissing) errors.industry = 'Industry is required';
            if (shouldValidateRegisteredOffice && !form.registeredOffice) errors.registeredOffice = 'Registered office is required';
            if (!form.inCorpDate) errors.inCorpDate = 'Incorporation date is required';
            
            // Validate BR field - if provided, left part must not be empty
            if (form.br && !validateBR(form.br)) {
                errors.br = 'BR prefix (before "-") is required';
            }
            
            if (Object.keys(errors).length > 0) {
                setFieldErrors(errors);
                setSnackbar({open: true, message: 'Please fill in all required fields', severity: 'error'});
                return false;
            }
            
            if (isEdit && form.companyId) {
                // Persist registeredOffice so O1 Issued Capital reads the same address
                await CompanyService.updateCompany(form.companyId, form);
                if (!hideActionButtons) {
                    onClose({refreshed: true, message: 'Update successful!', severity: 'success'});
                }
            } else {
                await CompanyService.createCompany(form);
                if (!hideActionButtons) {
                    onClose({refreshed: true, message: 'Create successful!', severity: 'success'});
                }
            }
            return true;
        } catch (e: any) {
            const msg = e?.message || 'Save failed';
            const fe: typeof fieldErrors = {_global: msg};
            const lower = String(msg).toLowerCase();
            if (lower.includes('name')) fe.companyName = msg;
            if (lower.includes('industry')) fe.industry = msg;
            if (lower.includes('registered office')) fe.registeredOffice = msg;
            if (lower.includes('incorporation date') || lower.includes('incorpdate')) fe.inCorpDate = msg;
            if (lower.includes('br')) fe.br = msg;
            setFieldErrors(fe);
            setSnackbar({open: true, message: msg, severity: 'error'});
            return false;
        } finally {
            setSaving(false);
        }
    };

    // 将 save 函数暴露给父组件，支持外部触发保存
    useEffect(() => {
        if (saveRef) saveRef.current = onSave;
    });

    const renderContent = () => (
        (loading || saving) ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                <Skeleton variant="circular" width={32} height={32} />
            </Box>
        ) : (
            <Box component="form" noValidate autoComplete="off" sx={{pt: 1}}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <InputField
                            align='left'
                            label="Company Name"
                            name="companyName"
                            value={form.companyName || ''}
                            fullWidth
                            onChange={(e) => setForm({...form, companyName: (e.target as HTMLInputElement).value})}
                            required
                            error={!form.companyName || !!fieldErrors.companyName}
                            helperText={fieldErrors.companyName || (!form.companyName ? 'Company name is required' : '')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {companyNameHistories.length > 0 && (
                                            <IconButton
                                                ref={nameHistoryBtnRef}
                                                size="small"
                                                edge="end"
                                                aria-label="company-name-history"
                                                onMouseEnter={() => setNameHistoryAnchorEl(nameHistoryBtnRef.current)}
                                                onMouseLeave={() => setNameHistoryAnchorEl(null)}
                                            >
                                                <HistoryIcon fontSize="small"/>
                                            </IconButton>
                                        )}
                                        <IconButton
                                            ref={crSearchBtnRef}
                                            size="small"
                                            edge="end"
                                            aria-label="cr-gov-company-search"
                                            disabled={!form.companyName?.trim() || crLookup.lookingUp}
                                            onClick={() => void crLookup.lookupFromIcon(crSearchBtnRef.current)}
                                        >
                                            {crLookup.lookingUp
                                                ? <CircularProgress size={16}/>
                                                : <TravelExploreOutlinedIcon fontSize="small"/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Popover
                            open={Boolean(nameHistoryAnchorEl)}
                            anchorEl={nameHistoryAnchorEl}
                            onClose={() => setNameHistoryAnchorEl(null)}
                            disableRestoreFocus
                            sx={{pointerEvents: 'none'}}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                        >
                            <Box sx={{p: 1.5, minWidth: 280, maxWidth: 420, maxHeight: 320, overflowY: 'auto'}}>
                                <Typography variant="subtitle2" sx={{mb: 1}}>
                                    Company Name History
                                </Typography>
                                {companyNameHistories.map((row, idx) => (
                                    <Box key={row.id ?? `${row.changeDate}-${row.content ?? row.officeAddress}-${idx}`}>
                                        {idx > 0 && <Divider sx={{my: 1}}/>}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Change Date: {row.changeDate || '—'}
                                            {row.yearOfAssessment ? ` · YOA: ${row.yearOfAssessment}` : ''}
                                        </Typography>
                                        <Typography variant="body2" sx={{mt: 0.25, whiteSpace: 'pre-wrap'}}>
                                            {row.content || row.officeAddress || '—'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Popover>
                        <Popover
                            open={Boolean(crLookup.popoverAnchor)}
                            anchorEl={crLookup.popoverAnchor}
                            onClose={crLookup.closePopover}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                            slotProps={{
                                paper: {
                                    sx: {
                                        width: {xs: 'min(96vw, 520px)', sm: 520},
                                        maxWidth: '96vw',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        maxHeight: 'min(70vh, 560px)',
                                    },
                                },
                            }}
                        >
                            {/* WHY: list scrolls; footer (actions + optional filter) stays pinned */}
                            <Box sx={{px: 1.5, pt: 1.5, pb: 0.5, flexShrink: 0}}>
                                <Typography variant="subtitle2">
                                    Confirm company from GOV
                                </Typography>
                            </Box>
                            <Box sx={{px: 1.5, flex: 1, minHeight: 0, overflowY: 'auto'}}>
                                {crLookup.filteredHits.length > 1 ? (
                                    <RadioGroup
                                        value={String(crLookup.selectedIndex)}
                                        onChange={(e) => crLookup.setSelectedIndex(Number(e.target.value))}
                                    >
                                        {crLookup.filteredHits.map((hit, idx) => (
                                            <FormControlLabel
                                                key={`${hit.brNo}-${hit.companyName}-${idx}`}
                                                value={String(idx)}
                                                control={<Radio size="small"/>}
                                                label={
                                                    <Box sx={{py: 0.5}}>
                                                        <Typography variant="body2">{hit.companyName || '—'}</Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            CN: {hit.companyChineseName || '—'} · InCorp: {hit.incorporationDate || '—'} · BR: {hit.brNo || '—'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        ))}
                                    </RadioGroup>
                                ) : crLookup.filteredHits.length === 1 ? (
                                    <Box sx={{mb: 1}}>
                                        <Typography variant="body2"><b>Company Name:</b> {crLookup.filteredHits[0]?.companyName || '—'}</Typography>
                                        <Typography variant="body2"><b>Chinese Name:</b> {crLookup.filteredHits[0]?.companyChineseName || '—'}</Typography>
                                        <Typography variant="body2"><b>InCorp Date:</b> {crLookup.filteredHits[0]?.incorporationDate || '—'}</Typography>
                                        <Typography variant="body2"><b>BR:</b> {crLookup.filteredHits[0]?.brNo || '—'}</Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{py: 2}}>
                                        No match in current list
                                    </Typography>
                                )}
                            </Box>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    flexShrink: 0,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Box sx={{flex: 1, minWidth: 0, pr: 1}}>
                                    {crLookup.showFilter && (
                                        <InputField
                                            size="small"
                                            align="left"
                                            label="Filter"
                                            name="crGovFilter"
                                            value={crLookup.filterQuery}
                                            onChange={(e) => crLookup.onFilterQueryChange((e.target as HTMLInputElement).value)}
                                            fullWidth
                                            sx={{maxWidth: 240}}
                                        />
                                    )}
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{flexShrink: 0}}>
                                    <Button size="small" onClick={crLookup.closePopover}>Cancel</Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={crLookup.filteredHits.length === 0}
                                        onClick={crLookup.convertSelected}
                                    >
                                        Convert
                                    </Button>
                                </Stack>
                            </Stack>
                        </Popover>
                    </Grid>
                    <Grid size={12}>
                        <InputField
                            align='left'
                            label="Company Chinese Name"
                            name="companyChineseName"
                            value={form.companyChineseName || ''}
                            fullWidth
                            onChange={(e) => setForm({...form, companyChineseName: (e.target as HTMLInputElement).value})}
                            InputProps={companyChineseNameHistories.length > 0 ? {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            ref={chineseNameHistoryBtnRef}
                                            size="small"
                                            edge="end"
                                            aria-label="company-chinese-name-history"
                                            onMouseEnter={() => setChineseNameHistoryAnchorEl(chineseNameHistoryBtnRef.current)}
                                            onMouseLeave={() => setChineseNameHistoryAnchorEl(null)}
                                        >
                                            <HistoryIcon fontSize="small"/>
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            } : undefined}
                        />
                        <Popover
                            open={Boolean(chineseNameHistoryAnchorEl)}
                            anchorEl={chineseNameHistoryAnchorEl}
                            onClose={() => setChineseNameHistoryAnchorEl(null)}
                            disableRestoreFocus
                            sx={{pointerEvents: 'none'}}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                        >
                            <Box sx={{p: 1.5, minWidth: 280, maxWidth: 420, maxHeight: 320, overflowY: 'auto'}}>
                                <Typography variant="subtitle2" sx={{mb: 1}}>
                                    Company Chinese Name History
                                </Typography>
                                {companyChineseNameHistories.map((row, idx) => (
                                    <Box key={row.id ?? `${row.changeDate}-${row.content ?? row.officeAddress}-${idx}`}>
                                        {idx > 0 && <Divider sx={{my: 1}}/>}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Change Date: {row.changeDate || '—'}
                                            {row.yearOfAssessment ? ` · YOA: ${row.yearOfAssessment}` : ''}
                                        </Typography>
                                        <Typography variant="body2" sx={{mt: 0.25, whiteSpace: 'pre-wrap'}}>
                                            {row.content || row.officeAddress || '—'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Popover>
                    </Grid>
                    {!hideIndustry && (
                        <Grid size={12}>
                            <MultiSelectField
                                label="Industry"
                                required
                                options={industries
                                    .filter((o) => o.industryId != null)
                                    .map((o) => ({
                                        value: Number(o.industryId),
                                        label: o.industryName || String(o.industryId),
                                    }))}
                                value={industryIds}
                                onChange={(ids) => setForm({...form, industry: ids})}
                                error={industryMissing || !!fieldErrors.industry}
                                helperText={fieldErrors.industry || (industryMissing ? 'Industry is required' : '')}
                            />
                        </Grid>
                    )}
                    {!hideRegisteredOffice && (
                        <Grid size={12}>
                            <InputField
                                align='left'
                                label="Registered Office"
                                name="registeredOffice"
                                value={form.registeredOffice || ''}
                                fullWidth
                                required={shouldValidateRegisteredOffice}
                                onChange={(e) => setForm({...form, registeredOffice: (e.target as HTMLInputElement).value})}
                                error={(shouldValidateRegisteredOffice && !form.registeredOffice) || !!fieldErrors.registeredOffice}
                                helperText={
                                    fieldErrors.registeredOffice
                                    || (shouldValidateRegisteredOffice && !form.registeredOffice
                                        ? 'Registered office is required'
                                        : (isEdit ? 'Synced with O1 Issued Capital.' : ''))
                                }
                                InputProps={officeHistories.length > 0 ? {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                ref={historyBtnRef}
                                                size="small"
                                                edge="end"
                                                aria-label="registered-office-history"
                                                onMouseEnter={() => setHistoryAnchorEl(historyBtnRef.current)}
                                                onMouseLeave={() => setHistoryAnchorEl(null)}
                                            >
                                                <HistoryIcon fontSize="small"/>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                } : undefined}
                            />
                            <Popover
                                open={Boolean(historyAnchorEl)}
                                anchorEl={historyAnchorEl}
                                onClose={() => setHistoryAnchorEl(null)}
                                disableRestoreFocus
                                sx={{pointerEvents: 'none'}}
                                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                            >
                                <Box sx={{p: 1.5, minWidth: 280, maxWidth: 420, maxHeight: 320, overflowY: 'auto'}}>
                                    <Typography variant="subtitle2" sx={{mb: 1}}>
                                        Registered Office History
                                    </Typography>
                                    {officeHistories.map((row, idx) => (
                                        <Box key={row.id ?? `${row.changeDate}-${row.officeAddress}-${idx}`}>
                                            {idx > 0 && <Divider sx={{my: 1}}/>}
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Change Date: {row.changeDate || '—'}
                                            </Typography>
                                            <Typography variant="body2" sx={{mt: 0.25, whiteSpace: 'pre-wrap'}}>
                                                {row.officeAddress || '—'}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Popover>
                        </Grid>
                    )}
                    <Grid size={12}>
                        <DatePicker
                            fullWidth
                            align='left'
                            label="Incorporation Date (公司成立日期)"
                            value={form.inCorpDate || null}
                            onChange={(v) => setForm({...form, inCorpDate: v || ''})}
                            compact
                            required
                            error={!form.inCorpDate || !!fieldErrors.inCorpDate}
                            helperText={fieldErrors.inCorpDate || (!form.inCorpDate ? 'Incorporation date is required' : '')}
                            popoverContent={
                                <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>
                                    You can find it in CR search. (你可以在公司注冊處網站找到。)
                                </Typography>
                            }
                        />
                    </Grid>
                    <Grid size={12}>
                        <InputField
                            type="br-suffix"
                            align='left'
                            label="Business Registration No."
                            name="BR"
                            value={form.br || ''}
                            fullWidth
                            separator="-"
                            rightPartDefault="000"
                            leftPartMaxLength={8}
                            popoverContent={
                                <Link
                                    href="https://www.gov.hk/en/residents/taxes/etax/services/brn_enquiry.htm"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    GovHK: Business Registration
                                </Link>
                            }
                            onChange={(e) => {
                                const newValue = (e.target as HTMLInputElement).value;
                                setForm({...form, br: newValue});
                                // Clear error when user types valid content
                                if (fieldErrors.br && validateBR(newValue)) {
                                    setFieldErrors({...fieldErrors, br: undefined});
                                }
                            }}
                            error={hasBRContent(form.br || '') && !validateBR(form.br || '')}
                            helperText={(hasBRContent(form.br || '') && !validateBR(form.br || '')) ? 'BR prefix is required' : ''}
                        />
                    </Grid>
                </Grid>
            </Box>
        )
    );

    // Content-only rendering without dialog header; Cancel at top-right
    if (mode === 'content') {
        return (
            <Box sx={{ bgcolor: theme.palette.background.default, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2, py: 1 }}>
                    <Button onClick={() => onClose()} variant="outlined" color="inherit"
                            sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Cancel</Button>
                </Box>
                <Box sx={{ p: 2, pt: 0 }}>
                    {renderContent()}
                </Box>
                {!hideActionButtons && (
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                        {isEdit ? (
                            canUpdate && (
                            <Button variant="contained" onClick={onSave} disabled={saving || hasErrors}
                                    sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Save</Button>
                            )
                        ) : (
                            canCreate && (
                            <Button variant="contained" onClick={onSave} disabled={saving || hasErrors}
                                    sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Create</Button>
                            )
                        )}
                    </Box>
                )}
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                />
            </Box>
        );
    }

    // Backward-compat inline box (with header)
    if (inline) {
        return (
            <Box sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Box component="span" sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Company' : 'Create Company'}</Box>
                    <IconButton aria-label="close" onClick={() => onClose()} sx={{ ml: 'auto' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.default }}>
                    {renderContent()}
                </Box>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button onClick={() => onClose()} variant="outlined" color="inherit"
                            sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Cancel</Button>
                    {isEdit ? (
                        canUpdate && (
                        <Button variant="contained" onClick={onSave} disabled={saving || hasErrors}
                                sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Save</Button>
                        )
                    ) : (
                        canCreate && (
                        <Button variant="contained" onClick={onSave} disabled={saving || hasErrors}
                                sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Create</Button>
                        )
                    )}
                </Box>
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                />
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            sx={{
                '& .MuiDialog-paper': {
                    bgcolor: theme.palette.background.paper,
                    boxShadow: theme.shadows[3],
                    minWidth: { xs: undefined, sm: 640 },
                }
            }}
        >
            <DialogTitle>
                {isEdit ? 'Edit Company' : 'Create Company'}
                <IconButton
                    aria-label="close"
                    onClick={() => onClose()}
                    sx={{position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500]}}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{bgcolor: theme.palette.background.default}}>
                {renderContent()}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()} variant="outlined" color="inherit"
                        sx={{textTransform: 'none', fontWeight: 600, minWidth: 100, mr: 2}}>Cancel</Button>
                {isEdit ? (
                    canUpdate && (
                    <Button variant="contained" onClick={onSave} disabled={saving || hasErrors}
                            sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Save</Button>
                    )
                ) : (
                    canCreate && (
                    <Button variant="contained" onClick={onSave} disabled={saving || hasErrors}
                            sx={{textTransform: 'none', fontWeight: 600, minWidth: 100}}>Create</Button>
                    )
                )}
            </DialogActions>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({...snackbar, open: false})}
            />
        </Dialog>
    );
}
